<template>
  <div class="platform-api-login">
    <p class="intro-tip">
      无需浏览器：扫码或手机验证码登录 {{ platformLabel }}。
    </p>

    <div class="tab-row sub">
      <button
        type="button"
        class="tab-btn"
        :class="{ active: method === 'qrcode' }"
        @click="switchMethod('qrcode')"
      >
        二维码
      </button>
      <button
        type="button"
        class="tab-btn"
        :class="{ active: method === 'phone' }"
        @click="switchMethod('phone')"
      >
        手机验证码
      </button>
      <el-button
        v-if="canSupportAutoLogin"
        type="primary"
        class="auto-login-btn"
        :loading="autoLogging"
        @click="oneKeyLogin"
      >
        ⚡ 一键自动登录
      </el-button>
    </div>
    <p v-if="autoHint" class="auto-hint">{{ autoHint }}</p>

    <div class="content-card">
      <div v-if="isLoggedIn && !forceRelogin" class="pane logged-pane">
        <p class="logged-title">{{ platformLabel }} 已登录</p>
        <p class="logged-hint">已保存 Cookie，如需重新登录请点击下方按钮。</p>
        <el-button type="primary" plain @click="forceRelogin = true; maybeStartQr()">
          重新登录
        </el-button>
      </div>

      <template v-else>
        <div v-if="method === 'qrcode'" class="pane qrcode-pane">
          <div v-if="qrLoading" class="center-msg">正在生成二维码…</div>
          <div v-else-if="qrImage" class="qr-wrap">
            <img :src="qrImage" alt="登录二维码" class="qr-img" />
            <p class="qr-hint">{{ qrStatusText }}</p>
          </div>
          <div v-else class="center-msg">
            <el-button type="primary" :loading="qrLoading" @click="startQr">获取二维码</el-button>
          </div>
        </div>

        <div v-else class="pane phone-pane">
          <el-form label-width="72px" @submit.prevent>
            <el-form-item label="手机号">
              <el-input v-model="phone" placeholder="11 位手机号" maxlength="11" />
            </el-form-item>
            <!-- captcha 图形验证码（百家号纯 API 需要时显示） -->
            <el-form-item v-if="phoneStatus === 'need_captcha' && qrImage" label="图形码">
              <div style="display:flex;gap:8px;align-items:center;">
                <img :src="qrImage" style="height:36px;border-radius:4px;" alt="captcha" />
                <el-input v-model="captchaCode" placeholder="请输入图中字母" maxlength="6" style="flex:1" />
              </div>
            </el-form-item>
            <el-form-item label="验证码">
              <div class="phone-code-row">
                <el-input v-model="smsCode" placeholder="短信验证码" maxlength="6" />
                <el-button :disabled="!canSendSms || smsSending" @click="sendSms">
                  {{ smsCountdown > 0 ? `${smsCountdown}s` : '发送验证码' }}
                </el-button>
              </div>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="phoneConfirming" :disabled="!sessionId" @click="confirmPhone">登录</el-button>
            </el-form-item>
            <p v-if="phoneStatusText" class="phone-status">{{ phoneStatusText }}</p>
            <!-- 兜底操作按钮：遇到 need_captcha / need_slider / failed 时提供备选路径 -->
            <div v-if="fallbackOptions.length" class="fallback-row">
              <template v-for="(fb, i) in fallbackOptions" :key="i">
                <el-button size="small" type="warning" @click="fallbackAction(fb)" :loading="fb._loading">
                  {{ fb.label }}
                </el-button>
              </template>
            </div>
            <!-- 滑动验证码截图 -->
            <div v-if="phoneStatus === 'need_slider' && qrImage" class="slider-tip">
              <img :src="qrImage" class="slider-shot" alt="滑动验证码截图" />
              <p class="slider-hint">请在浏览器中找到验证码窗口，拖动滑块完成验证后点击「手动完成滑动验证后继续」</p>
            </div>
          </el-form>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'

const props = defineProps({
  accountId: { type: String, required: true },
  apiBase: { type: String, required: true },
  platform: { type: String, required: true },
  platformLabel: { type: String, default: '' },
  hasCookie: { type: Boolean, default: false },
})

const emit = defineEmits(['success'])

const PLATFORM_LABELS = {
  zhihu: '知乎',
  douyin: '抖音',
  toutiao: '头条号',
  baijiahao: '百家号',
}

const platformName = computed(() => props.platformLabel || PLATFORM_LABELS[props.platform] || props.platform)

const method = ref('qrcode')
const forceRelogin = ref(false)
const isLoggedIn = ref(false)

const sessionId = ref('')
const qrImage = ref('')
const qrLoading = ref(false)
const qrStatus = ref('')
let pollTimer = null

const phone = ref('')
const smsCode = ref('')
const smsSending = ref(false)
const smsCountdown = ref(0)
const phoneConfirming = ref(false)
const autoLogging = ref(false)
const autoHint = ref('')
const fallbackOptions = ref([])
let smsTimer = null

// 一键自动登录：百家号/抖音/头条 支持手机号自动登录
const canSupportAutoLogin = computed(() => props.platform !== 'zhihu')

const oneKeyLogin = async () => {
  autoLogging.value = true
  autoHint.value = ''
  try {
    const res = await axios.post(`${props.apiBase}/cookies/platform-api/auto-login`, {
      account_id: props.accountId,
      platform: props.platform,
    })
    const data = res.data
    resetSession()
    sessionId.value = data.session_id || ''
    phoneMask.value = data.phone_mask || ''
    phoneStatus.value = data.status || ''
    if (data.status === 'confirmed' && data.saved?.has_cookie) {
      ElMessage.success(`${platformName.value} 上号成功`)
      autoHint.value = `${platformName.value} 已自动登录`
      finishLogin(data)
      return
    }
    autoHint.value = `${platformName.value} 已自动发送验证码，系统正在自动接收并上号…`
    ElMessage.success('验证码已自动发送，正在自动读取并登录，无需手动操作')
    pollAutoResult()
  } catch (e) {
    const detail = e.response?.data?.detail || '一键登录失败'
    ElMessage.error(detail)
    autoHint.value = detail
  } finally {
    autoLogging.value = false
  }
}

const pollAutoResult = () => {
  clearPoll()
  pollTimer = setInterval(async () => {
    if (!sessionId.value) return
    try {
      const res = await axios.get(`${props.apiBase}/cookies/platform-api/${sessionId.value}`)
      const data = res.data
      phoneStatus.value = data.status || ''
      fallbackOptions.value = (data.fallback_options || []).map((f) => ({ ...f, _loading: false }))
      qrImage.value = data.qr_image_data_url || ''
      if (data.status === 'confirmed' && data.saved?.has_cookie) {
        clearPoll()
        autoHint.value = `${platformName.value} 已自动登录成功`
        ElMessage.success(`${platformName.value} 自动上号成功`)
        finishLogin(data)
      } else if (data.status === 'failed' || data.status === 'expired') {
        clearPoll()
        autoHint.value = data.message || '自动登录失败'
        ElMessage.warning(data.message || '自动登录失败')
        // 保留兜底选项
      }
    } catch (e) {
      clearPoll()
      ElMessage.error(e.response?.data?.detail || '查询登录状态失败')
    }
  }, 2000)
}

const qrStatusText = computed(() => {
  if (qrStatus.value === 'waiting_scan') return '请使用手机 App 扫码'
  if (qrStatus.value === 'scanned') return '已扫码，请在手机上确认'
  if (qrStatus.value === 'confirmed') return '登录成功'
  if (qrStatus.value === 'expired') return '二维码已过期，请重新获取'
  if (qrStatus.value === 'failed') return '登录失败，请重试'
  return '正在生成二维码…'
})

const phoneStatus = ref('')
const captchaCode = ref('')
const phoneStatusText = computed(() => {
  if (phoneStatus.value === 'code_sent') return '验证码已发送至 ' + (phoneMask.value || '您的手机')
  if (phoneStatus.value === 'confirmed') return '登录成功'
  if (phoneStatus.value === 'failed') return '登录失败，请重试'
  if (phoneStatus.value === 'expired') return '验证码已过期，请重新发送'
  if (phoneStatus.value === 'need_captcha') return '请先输入图形验证码'
  return ''
})

const phoneMask = ref('')

const canSendSms = computed(() => /^1\d{10}$/.test(phone.value.trim()))

const clearPoll = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const resetSession = () => {
  clearPoll()
  sessionId.value = ''
  qrImage.value = ''
  qrStatus.value = ''
  qrLoading.value = false
  phoneStatus.value = ''
  phoneMask.value = ''
  autoHint.value = ''
  captchaCode.value = ''
  fallbackOptions.value = []
}

const maybeStartQr = () => {
  if (method.value !== 'qrcode') return
  if (isLoggedIn.value && !forceRelogin.value) return
  startQr()
}

const switchMethod = (m) => {
  if (method.value === m) return
  method.value = m
  resetSession()
  maybeStartQr()
}

const finishLogin = (data) => {
  forceRelogin.value = false
  isLoggedIn.value = true
  emit('success', {
    platform: props.platform,
    account_id: data?.account_id || props.accountId,
    ...(data || {}),
  })
}

const pollQr = () => {
  clearPoll()
  pollTimer = setInterval(async () => {
    if (!sessionId.value) return
    try {
      const res = await axios.get(`${props.apiBase}/cookies/platform-api/${sessionId.value}`)
      const data = res.data
      qrStatus.value = data.status || ''
      if (data.status === 'confirmed' && data.saved?.has_cookie) {
        clearPoll()
        ElMessage.success(`${platformName.value} 登录成功`)
        finishLogin(data)
      } else if (data.status === 'expired' || data.status === 'failed') {
        clearPoll()
        ElMessage.warning(data.message || '二维码已失效，请重新获取')
        qrImage.value = ''
      }
    } catch (e) {
      const detail = e.response?.data?.detail
      if (detail) {
        clearPoll()
        ElMessage.error(detail)
        forceRelogin.value = true
      }
    }
  }, 2000)
}

const startQr = async () => {
  resetSession()
  qrLoading.value = true
  try {
    const res = await axios.post(`${props.apiBase}/cookies/platform-api/qrcode`, {
      account_id: props.accountId,
      platform: props.platform,
    })
    sessionId.value = res.data.session_id
    qrImage.value = res.data.qr_image_data_url || ''
    if (res.data.status === 'confirmed') {
      ElMessage.success(`${platformName.value} 已登录`)
      finishLogin(res.data)
      return
    }
    if (!qrImage.value) {
      ElMessage.error('未能生成二维码，请重试')
      return
    }
    qrStatus.value = res.data.status || 'waiting_scan'
    pollQr()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '获取二维码失败')
  } finally {
    qrLoading.value = false
  }
}

const startSmsCountdown = () => {
  smsCountdown.value = 60
  if (smsTimer) clearInterval(smsTimer)
  smsTimer = setInterval(() => {
    smsCountdown.value -= 1
    if (smsCountdown.value <= 0) {
      clearInterval(smsTimer)
      smsTimer = null
    }
  }, 1000)
}

const sendSms = async () => {
  if (!canSendSms.value) return
  smsSending.value = true
  phoneStatus.value = ''
  try {
    const res = await axios.post(`${props.apiBase}/cookies/platform-api/phone/send`, {
      account_id: props.accountId,
      platform: props.platform,
      phone: phone.value.trim(),
      captcha_code: captchaCode.value || undefined,
    })
    sessionId.value = res.data.session_id || ''
    phoneStatus.value = res.data.status || 'code_sent'
    if (res.data.phone_mask) phoneMask.value = res.data.phone_mask
    if (res.data.status === 'failed') {
      ElMessage.error(res.data.message || '发送失败')
      return
    }
    if (res.data.status !== 'confirmed') {
      ElMessage.success(res.data.message || '验证码已发送')
      startSmsCountdown()
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '发送失败')
  } finally {
    smsSending.value = false
  }
}

const confirmPhone = async () => {
  if (!sessionId.value) {
    ElMessage.warning('请先发送验证码')
    return
  }
  if (!smsCode.value.trim()) {
    ElMessage.warning('请输入验证码')
    return
  }
  phoneConfirming.value = true
  phoneStatus.value = ''
  try {
    const res = await axios.post(`${props.apiBase}/cookies/platform-api/phone/confirm`, {
      session_id: sessionId.value,
      code: smsCode.value.trim(),
    })
    const data = res.data
    phoneStatus.value = data.status || ''
    if (data.status === 'confirmed' && data.saved?.has_cookie) {
      ElMessage.success(`${platformName.value} 登录成功`)
      finishLogin(data)
    } else if (data.status === 'failed' || data.status === 'expired') {
      ElMessage.error(data.message || '登录失败')
      sessionId.value = ''
    } else {
      ElMessage.success('验证码已提交，正在验证…')
      // 轮询等待登录结果
      pollPhoneResult()
    }
  } catch (e) {
    const detail = e.response?.data?.detail || '登录失败'
    ElMessage.error(detail)
    sessionId.value = ''
    smsCode.value = ''
    phoneStatus.value = 'failed'
  } finally {
    phoneConfirming.value = false
  }
}

const pollPhoneResult = () => {
  clearPoll()
  let attempts = 0
  const maxAttempts = 30
  pollTimer = setInterval(async () => {
    attempts += 1
    if (!sessionId.value || attempts > maxAttempts) {
      clearPoll()
      if (attempts > maxAttempts) {
        ElMessage.warning('验证超时，请重新发送验证码')
        sessionId.value = ''
      }
      return
    }
    try {
      const res = await axios.get(`${props.apiBase}/cookies/platform-api/${sessionId.value}`)
      const data = res.data
      phoneStatus.value = data.status || ''
      fallbackOptions.value = (data.fallback_options || []).map((f) => ({ ...f, _loading: false }))
      qrImage.value = data.qr_image_data_url || ''
      if (data.status === 'confirmed' && data.saved?.has_cookie) {
        clearPoll()
        ElMessage.success(`${platformName.value} 登录成功`)
        finishLogin(data)
      } else if (data.status === 'failed' || data.status === 'expired') {
        clearPoll()
        ElMessage.error(data.message || '登录失败')
        // 不回空 sessionId，让兜底按钮可用
      }
    } catch (e) {
      clearPoll()
      ElMessage.error(e.response?.data?.detail || '验证状态查询失败')
      sessionId.value = ''
    }
  }, 2000)
}

watch(
  () => [props.accountId, props.hasCookie],
  () => {
    isLoggedIn.value = !!props.hasCookie
    forceRelogin.value = false
    phone.value = ''
    smsCode.value = ''
    resetSession()
    maybeStartQr()
  },
  { immediate: true },
)

const fallbackAction = async (fb) => {
  fb._loading = true
  try {
    if (fb.action === 'switch_qrcode') {
      method.value = 'qrcode'
      resetSession()
      await startQr()
      return
    }
    if (fb.action === 'retry_captcha' || fb.action === 'retry_phone') {
      await sendSms()
      return
    }
    if (fb.action === 'retry_slider') {
      const res = await axios.post(`${props.apiBase}/cookies/platform-api/phone/retry-slider`, {
        session_id: sessionId.value,
      })
      const data = res.data
      phoneStatus.value = data.status || ''
      fallbackOptions.value = (data.fallback_options || []).map((f) => ({ ...f, _loading: false }))
      qrImage.value = data.qr_image_data_url || ''
      if (data.status === 'code_sent') {
        ElMessage.success(data.message || '验证码已重新发送')
        smsCode.value = ''
        pollPhoneResult()
      } else if (data.status === 'need_slider') {
        ElMessage.warning(data.message || '滑块仍未通过，请手动拖拽后重试')
      } else {
        ElMessage.warning(data.message || '操作未完成')
      }
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '操作失败')
  } finally {
    fb._loading = false
  }
}

onBeforeUnmount(() => {
  clearPoll()
  if (smsTimer) clearInterval(smsTimer)
})
</script>

<style scoped>
.platform-api-login {
  padding: 4px 0 8px;
}

.intro-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.tab-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.tab-row.sub {
  margin-bottom: 4px;
}

.auto-login-btn {
  margin-left: auto;
}

.auto-hint {
  margin: 0 0 10px;
  font-size: 12px;
  color: #67c23a;
  line-height: 1.4;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--el-text-color-regular);
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.tab-btn:hover {
  color: var(--el-text-color-primary);
}

.tab-btn.active {
  background: var(--el-fill-color-dark);
  color: var(--el-text-color-primary);
  font-weight: 500;
}

.content-card {
  background: #1c1c1e;
  border-radius: 12px;
  padding: 24px 20px;
  min-height: 280px;
}

.pane {
  width: 100%;
}

.logged-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 232px;
  gap: 12px;
  text-align: center;
}

.logged-title {
  margin: 0;
  font-size: 16px;
  color: #67c23a;
  font-weight: 500;
}

.logged-hint {
  margin: 0;
  font-size: 13px;
  color: #a1a1a6;
  max-width: 320px;
  line-height: 1.5;
}

.qrcode-pane {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 232px;
}

.center-msg {
  color: #a1a1a6;
  font-size: 14px;
  text-align: center;
}

.qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-img {
  width: 200px;
  height: 200px;
  background: #fff;
  border-radius: 8px;
  padding: 8px;
  object-fit: contain;
}

.qr-hint {
  margin: 0;
  font-size: 13px;
  color: #a1a1a6;
}

.phone-pane :deep(.el-form-item__label) {
  color: #a1a1a6;
}

.phone-pane :deep(.el-input__wrapper) {
  background: #2c2c2e;
  box-shadow: none;
}

.phone-pane :deep(.el-input__inner) {
  color: #f5f5f7;
}

.phone-code-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.phone-code-row .el-input {
  flex: 1;
}

.phone-status {
  margin: 0 0 0 72px;
  font-size: 13px;
  color: #a1a1a6;
  line-height: 1.5;
}

.fallback-row {
  margin: 4px 0 0 72px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.slider-tip {
  margin: 8px 0 0 72px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.slider-shot {
  max-width: 280px;
  border-radius: 6px;
  border: 1px solid #444;
}
.slider-hint {
  margin: 0;
  font-size: 12px;
  color: #a1a1a6;
  line-height: 1.5;
}
</style>